import type { Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { errorMessages } from '~/constants/errorMessages.ts';
import type { TAutocompleteOption } from '~/components/AutocompleteField/types.ts';
import { getStringValue } from '~/modules/guarantees/utils.ts';
import {
  GuaranteeSecurityType,
  GuaranteesAccountPlacementStatus,
  GuaranteesExecutorMainDealConditionsStatus,
  type TGuaranteesApplication,
  type TGuaranteesExecutionForm,
  type TPatchGuaranteesExecutionFormPayload,
} from '~/store/guarantees/types.ts';
import {
  ACCOUNT_PLACEMENT_STATUS_OPTIONS,
  EXECUTOR_MAIN_DEAL_CONDITIONS_STATUS_OPTIONS,
} from './constants.ts';
import {
  GuaranteesUakdoControlFieldName,
  type TGuaranteesUakdoControlFormValues,
} from './types.ts';

const hasAutocompleteValue = (value: unknown): boolean =>
  !!(value as TAutocompleteOption | null | undefined)?.value;

const findOption = <TOption extends TAutocompleteOption>(
  options: readonly TOption[],
  value: unknown
): TOption | null => {
  const normalizedValue = getStringValue(value);

  return options.find((option) => option.value === normalizedValue) ?? null;
};

export const getGuaranteesUakdoSecurityType = (
  application?: TGuaranteesApplication | null
): GuaranteeSecurityType | '' => {
  const root = application?.root as Record<string, unknown> | undefined;
  const security = application?.security as Record<string, unknown> | undefined;
  const securityType = getStringValue(
    root?.securityType ||
      root?.security_type ||
      security?.securityType ||
      security?.security_type ||
      security?.type
  );

  return securityType === GuaranteeSecurityType.COVERAGE_ACCOUNT ||
    securityType === GuaranteeSecurityType.DEPOSIT_RIGHTS
    ? securityType
    : '';
};

export const createGuaranteesUakdoControlValidationSchema = (
  securityType: GuaranteeSecurityType | ''
) =>
  yup.object({
    [GuaranteesUakdoControlFieldName.ExecutorMainDealConditionsStatus]: yup
      .mixed<TAutocompleteOption>()
      .nullable()
      .test('executor-main-deal-conditions-required', errorMessages.OPTION_REQUIRED, hasAutocompleteValue),
    [GuaranteesUakdoControlFieldName.UnfulfilledConditionsDetails]: yup
      .string()
      .trim()
      .when(GuaranteesUakdoControlFieldName.ExecutorMainDealConditionsStatus, {
        is: (value: TAutocompleteOption | null) =>
          value?.value === GuaranteesExecutorMainDealConditionsStatus.NOT_PERFORMED,
        then: (schema) => schema.required(errorMessages.FIELD_REQUIRED),
      }),
    [GuaranteesUakdoControlFieldName.CoverageAccountPlacementStatus]: yup
      .mixed<TAutocompleteOption>()
      .nullable()
      .test(
        'coverage-account-placement-required',
        errorMessages.OPTION_REQUIRED,
        (value) => securityType !== GuaranteeSecurityType.COVERAGE_ACCOUNT || hasAutocompleteValue(value)
      ),
    [GuaranteesUakdoControlFieldName.DepositAccountPlacementStatus]: yup
      .mixed<TAutocompleteOption>()
      .nullable()
      .test(
        'deposit-account-placement-required',
        errorMessages.OPTION_REQUIRED,
        (value) => securityType !== GuaranteeSecurityType.DEPOSIT_RIGHTS || hasAutocompleteValue(value)
      ),
  });

export const createGuaranteesUakdoControlResolver = (
  securityType: GuaranteeSecurityType | ''
): Resolver<TGuaranteesUakdoControlFormValues> =>
  yupResolver(createGuaranteesUakdoControlValidationSchema(securityType)) as Resolver<
    TGuaranteesUakdoControlFormValues
  >;

export const getGuaranteesUakdoControlInitialValues = (
  executionForm?: TGuaranteesExecutionForm | null
): TGuaranteesUakdoControlFormValues => ({
  [GuaranteesUakdoControlFieldName.ExecutorMainDealConditionsStatus]: findOption(
    EXECUTOR_MAIN_DEAL_CONDITIONS_STATUS_OPTIONS,
    executionForm?.executorMainDealConditionsStatus
  ),
  [GuaranteesUakdoControlFieldName.UnfulfilledConditionsDetails]: getStringValue(
    executionForm?.unfulfilledConditionsDetails
  ),
  [GuaranteesUakdoControlFieldName.CoverageAccountPlacementStatus]: findOption(
    ACCOUNT_PLACEMENT_STATUS_OPTIONS,
    executionForm?.coverageAccountPlacementStatus
  ),
  [GuaranteesUakdoControlFieldName.DepositAccountPlacementStatus]: findOption(
    ACCOUNT_PLACEMENT_STATUS_OPTIONS,
    executionForm?.depositAccountPlacementStatus
  ),
});

export const buildGuaranteesUakdoControlPayload = (
  values: TGuaranteesUakdoControlFormValues,
  securityType: GuaranteeSecurityType | ''
): TPatchGuaranteesExecutionFormPayload => {
  const payload: TPatchGuaranteesExecutionFormPayload = {
    executorMainDealConditionsStatus: values.executorMainDealConditionsStatus
      ?.value as GuaranteesExecutorMainDealConditionsStatus,
    unfulfilledConditionsDetails: getStringValue(values.unfulfilledConditionsDetails),
  };

  if (securityType === GuaranteeSecurityType.COVERAGE_ACCOUNT) {
    payload.coverageAccountPlacementStatus = values.coverageAccountPlacementStatus
      ?.value as GuaranteesAccountPlacementStatus;
  }

  if (securityType === GuaranteeSecurityType.DEPOSIT_RIGHTS) {
    payload.depositAccountPlacementStatus = values.depositAccountPlacementStatus
      ?.value as GuaranteesAccountPlacementStatus;
  }

  return payload;
};
